package com.dotpx.ambient

import kotlinx.serialization.json.*

/** Platform storage must write atomically and report failures without exposing credentials. */
interface PurchaseLinkStorage {
    fun write(value: String?): Boolean
}

/** Host performs this request without redirects, using HTTPS and a bounded response body. */
data class PurchaseLinkRequest(
    val id: Long,
    val path: String,
    val method: String,
    val body: String?,
    val authToken: String?,
) {
    fun toJson(): String = buildJsonObject {
        put("id", id); put("path", path); put("method", method)
        put("body", body?.let(::JsonPrimitive) ?: JsonNull)
        put("authToken", authToken?.let(::JsonPrimitive) ?: JsonNull)
    }.toString()
}

data class PurchaseLinkDeviceSnapshot(
    val linked: Boolean,
    val pending: Boolean,
    val premium: Boolean,
    val lifetime: Boolean,
    val busy: Boolean,
    val validUntil: Long,
    val reason: String,
    val error: String,
    val errorStatus: Int,
    val qrSize: Int,
    val qrModules: String,
    val linkUrl: String?,
    val expiresAt: Long,
    val nextRefreshAt: Long,
    val hasDevice: Boolean,
    val accessExpired: Boolean,
    val completedAction: String,
) {
    fun toJson(): String = buildJsonObject {
        put("linked", linked); put("pending", pending); put("premium", premium)
        put("lifetime", lifetime); put("busy", busy); put("validUntil", validUntil)
        put("reason", reason); put("error", error); put("errorStatus", errorStatus)
        put("qrSize", qrSize); put("qrModules", qrModules)
        put("linkUrl", linkUrl?.let(::JsonPrimitive) ?: JsonNull)
        put("expiresAt", expiresAt); put("nextRefreshAt", nextRefreshAt); put("hasDevice", hasDevice)
        put("accessExpired", accessExpired); put("completedAction", completedAction)
    }.toString()
}

data class PurchaseLinkProof(
    val platform: String,
    val appUserId: String,
    val receipt: String,
    val productId: String? = null,
)

data class PurchaseLinkedDevice(val id: String, val name: String, val platform: String)

data class PurchaseLinkOwnerSnapshot(
    val devices: List<PurchaseLinkedDevice>,
    val approvalName: String?,
    val busy: Boolean,
    val error: String,
    val errorStatus: Int,
    val completedAction: String,
)

internal val linkingJson = Json
internal val linkingTokenPattern = Regex("[A-Za-z0-9_-]{43}")
internal val linkingDeviceIdPattern = Regex("[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}")
internal fun jsonObject(value: String?): JsonObject? = value?.takeIf { it.length <= 1_048_576 }?.let {
    runCatching { linkingJson.parseToJsonElement(it) as? JsonObject }.getOrNull()
}
internal fun JsonObject.string(name: String): String? = (this[name] as? JsonPrimitive)?.takeIf { it.isString }?.content
internal fun JsonObject.boolean(name: String): Boolean = (this[name] as? JsonPrimitive)?.takeIf { !it.isString }?.booleanOrNull == true
internal fun JsonObject.millis(name: String): Long? = (this[name] as? JsonPrimitive)?.takeIf { !it.isString }?.longOrNull
    ?.takeIf { it in 0..9_007_199_254_740_991L }
internal fun JsonObject.number(name: String): Int? = (this[name] as? JsonPrimitive)?.takeIf { !it.isString }?.intOrNull
internal fun persist(storage: PurchaseLinkStorage, value: String?): Boolean = runCatching { storage.write(value) }.getOrDefault(false)
internal fun errorCode(body: String, fallback: String): String = jsonObject(body)?.string("error")
    ?.takeIf { it.length <= 128 && it.matches(Regex("[A-Za-z0-9_]+")) } ?: fallback
